import React, { useMemo } from 'react';
import type { CellContext, Table, HeaderGroup, Header, Row, Cell } from '@tanstack/react-table';
import type { CompassCardComponent, CompassCardComponentProps } from '@scale/compass-ui/types';
import type { TagUntagUsersContext } from '@scale/genai-compass-shared/cards/tag_untag_users';
import { Box, Code, DataTable, Flex, Icon, Text } from '@scale/scaleui-radix';
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-regular-svg-icons';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import useTagUntagUsersCard from './useTagUntagUsersCard';

const TagUntagUsersCardComponent: CompassCardComponent<TagUntagUsersContext> = ({
  context,
  updateCardContext,
  updateCardCanSubmit,
  previousCardData,
}: CompassCardComponentProps<TagUntagUsersContext>) => {
  const { userIdHeader, tagIdHeader, actionHeader } = useTagUntagUsersCard(
    context,
    updateCardContext,
    updateCardCanSubmit,
    previousCardData,
  );

  return (
    <Flex direction="row" gap="9" justify="between">
      <Box width="full">
        <SanitizedMarkdown
          text={`**This will apply tag/untag operations per row based on the action column.**\n\nEnsure the following columns are present in the prior card:\n\n**Note:** Operations are idempotent - 'tag' only applies if the user doesn't have the tag, 'untag' only removes if the user has the tag.`}
        />

        <Box mt="4" mb="4">
          <ParametersTable />
        </Box>
      </Box>

      <Flex direction="column" gap="4" minWidth="400px">
        <Flex justify="end">
          <Flex direction="column" gap="2">
            <Flex
              align="center"
              gap="2"
              className={userIdHeader ? 'text-neutral-600' : 'text-red-600'}
            >
              <Icon
                icon={userIdHeader ? faCheckCircle : faXmarkCircle}
                size="sm"
                label="Info"
                className={userIdHeader ? 'text-primary-600' : 'text-red-600'}
              />
              <Text as="p" size="2">
                {userIdHeader ? (
                  <>
                    Using column <Code>{userIdHeader}</Code> for User ID
                  </>
                ) : (
                  'No userId column found'
                )}
              </Text>
            </Flex>
            <Flex
              align="center"
              gap="2"
              className={tagIdHeader ? 'text-neutral-600' : 'text-red-600'}
            >
              <Icon
                icon={tagIdHeader ? faCheckCircle : faXmarkCircle}
                size="sm"
                label="Info"
                className={tagIdHeader ? 'text-primary-600' : 'text-red-600'}
              />
              <Text as="p" size="2">
                {tagIdHeader ? (
                  <>
                    Using column <Code>{tagIdHeader}</Code> for Tag ID
                  </>
                ) : (
                  'No tagId column found'
                )}
              </Text>
            </Flex>
            <Flex
              align="center"
              gap="2"
              className={actionHeader ? 'text-neutral-600' : 'text-red-600'}
            >
              <Icon
                icon={actionHeader ? faCheckCircle : faXmarkCircle}
                size="sm"
                label="Info"
                className={actionHeader ? 'text-primary-600' : 'text-red-600'}
              />
              <Text as="p" size="2">
                {actionHeader ? (
                  <>
                    Using column <Code>{actionHeader}</Code> for Action
                  </>
                ) : (
                  'No action column found'
                )}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

type ParamMetadata = {
  description: string;
  required: boolean;
};

type ParameterTableRow = {
  columnName: string;
  description: string;
};

const ParametersTable = () => {
  const paramMetadata: Record<string, ParamMetadata> = useMemo(
    () => ({
      userId: {
        description:
          'The user id to apply the tag operation to (must be named exactly "userId" or "user_id")',
        required: true,
      },
      tagId: {
        description:
          'The tag id to add or remove for this user (must be named exactly "tagId" or "tag_id")',
        required: true,
      },
      action: {
        description: 'The action to perform: "tag" or "untag" (must be named exactly "action")',
        required: true,
      },
    }),
    [],
  );

  const data = useMemo(
    () =>
      Object.entries(paramMetadata).map(([columnName, { description, required }]) => ({
        columnName,
        description: required ? `(Required) - ${description}` : `(Optional) - ${description}`,
      })),
    [paramMetadata],
  );

  const columnHelper = DataTable.createColumnHelper<ParameterTableRow>();

  const ColumnNameCell = ({ value }: { value: string }) => <Code>{value}</Code>;
  ColumnNameCell.displayName = 'ColumnNameCell';

  const DescriptionCell = ({ value }: { value: string }) => <>{value}</>;
  DescriptionCell.displayName = 'DescriptionCell';

  const renderColumnNameCell = (info: CellContext<ParameterTableRow, string>) => (
    <ColumnNameCell value={info.getValue()} />
  );
  renderColumnNameCell.displayName = 'RenderColumnNameCell';

  const renderDescriptionCell = (info: CellContext<ParameterTableRow, string>) => (
    <DescriptionCell value={info.getValue()} />
  );
  renderDescriptionCell.displayName = 'RenderDescriptionCell';

  const columns = [
    columnHelper.accessor('columnName', {
      id: 'columnName',
      header: 'Column Name',
      cell: renderColumnNameCell,
    }),
    columnHelper.accessor('description', {
      id: 'description',
      header: 'Description',
      cell: renderDescriptionCell,
    }),
  ];

  return (
    <DataTable.Root columns={columns} data={data}>
      {({ table }: { table: Table<ParameterTableRow> }) => (
        <DataTable.Table>
          <DataTable.Header>
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<ParameterTableRow>) => (
              <DataTable.HeaderGroup key={headerGroup.id}>
                {headerGroup.headers.map((header: Header<ParameterTableRow, unknown>) => (
                  <DataTable.ColumnHeaderCell key={header.id} header={header} />
                ))}
              </DataTable.HeaderGroup>
            ))}
          </DataTable.Header>
          <DataTable.Body>
            {table.getRowModel().rows.map((row: Row<ParameterTableRow>) => (
              <DataTable.Row key={row.id} row={row}>
                {row.getVisibleCells().map((cell: Cell<ParameterTableRow, unknown>) => (
                  <DataTable.Cell key={cell.id} cell={cell} />
                ))}
              </DataTable.Row>
            ))}
          </DataTable.Body>
        </DataTable.Table>
      )}
    </DataTable.Root>
  );
};

ParametersTable.displayName = 'ParametersTable';

export default React.memo(TagUntagUsersCardComponent);
